import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync } from '@angular/router';
import { ToastService } from '@app/portal/services/toast-service';
import { RouterService } from '@app/routing/router.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { containsHttpError } from '@app/utils/error/portal-error';
import { notNullish } from '@app/utils/rxjs';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EntityExistsGuard implements CanActivate {
  private _enterpriseNavigationFailed$ = new Subject<string>();

  get enterpriseNavigationFailed$(): Observable<string> {
    return this._enterpriseNavigationFailed$.asObservable();
  }

  private _userNavigationFailed$ = new Subject<string>();

  get userNavigationFailed$(): Observable<string> {
    return this._userNavigationFailed$.asObservable();
  }

  private subscription: Subscription | null = null;

  constructor(
    private enterpriseContextService: EnterpriseContextService,
    private routerService: RouterService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private endUserContextService: EndUserContextService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): MaybeAsync<GuardResult> {
    const { enterpriseId, userId } = route.params;

    // We need to let the navigation go through before subscribing to the cached instance.
    // Otherwise, we'd use the wrong route parameters.
    if (this.subscription != null) {
      this.subscription.unsubscribe();
    }

    this.subscription = timer(0)
      .pipe(
        mergeMap(() => {
          if (userId != null) {
            const enterprise$ = this.verifyEnterpriseExistence(enterpriseId);
            const user$ = this.verifyUserExistence(userId);

            return forkJoin([enterprise$, user$]);
          } else {
            return this.verifyEnterpriseExistence(enterpriseId).pipe(
              map((enterpriseFailed) => [enterpriseFailed])
            );
          }
        }),
        first()
      )
      .subscribe(([enterpriseError, userError]) => {
        this.subscription = null;

        if (enterpriseError) {
          this.toastService.warning(
            this.translateService.instant('enterprise.not.found', {
              enterpriseName: enterpriseId,
            })
          );

          this._enterpriseNavigationFailed$.next(enterpriseId);

          this.routerService
            .navigateToRoute(
              'service-provider-enterprises',
              {},
              {
                replaceUrl: true,
              }
            )
            .then(() => {
              this.enterpriseContextService.enterprise.clear();
              this.endUserContextService.endUser.clear();
            });
        } else if (userError) {
          this.toastService.warning(
            this.translateService.instant('end-user.not.found', {
              loginName: userId,
            })
          );

          this._userNavigationFailed$.next(userId);

          this.routerService
            .navigateToRoute(
              'enterprise-users',
              {},
              {
                replaceUrl: true,
              }
            )
            .then(() => {
              this.endUserContextService.endUser.clear();
            });
        }
      });

    return true;
  }

  private verifyEnterpriseExistence(enterpriseId: string): Observable<boolean> {
    const enterpriseData$ = this.enterpriseContextService.enterprise.data$.pipe(
      filter((enterprise) => enterprise.c5.name === enterpriseId)
    );
    const error$ = this.enterpriseContextService.enterprise.error$.pipe(notNullish());

    return merge(enterpriseData$, error$).pipe(
      first(),
      map((dataOrError) =>
        containsHttpError(dataOrError, { exception: 'repositories.c5.C5NotFoundException' })
      )
    );
  }

  private verifyUserExistence(userId: string): Observable<boolean> {
    const userData$ = this.endUserContextService.endUser.data$.pipe(
      filter((enterprise) => enterprise.localData?.user.loginName === userId)
    );
    const error$ = this.endUserContextService.endUser.error$.pipe(notNullish());

    return merge(userData$, error$).pipe(
      first(),
      map((dataOrError) =>
        containsHttpError(dataOrError, { exception: 'repositories.c5.C5NotFoundException' })
      )
    );
  }
}
